import React from "react";

/**
 * Buttons for the patient/caregiver profile modals.
 *
 * These modals sit inside the AngularJS shell, so their buttons are plain
 * `<button>` elements carrying the same bootstrap/inspinia classes the Angular
 * templates use — no Chakra button styling — which keeps them pixel-identical
 * to their Angular counterparts.
 */
export type EntityModalButtonKind = "blue" | "orange" | "red" | "white";
export type EntityModalButtonVariant = "solid" | "outline" | "link";
export type EntityModalButtonSize = "xs" | "sm" | "md";

type Props = {
  children?: React.ReactNode;
  /** FontAwesome 4 class, e.g. `fa fa-plus`. Replaced by a spinner while loading. */
  iconClassName?: string;
  /** Renders a square button sized for a bare icon. */
  isIconOnly?: boolean;
  isLoading?: boolean;
  kind?: EntityModalButtonKind;
  size?: EntityModalButtonSize;
  variant?: EntityModalButtonVariant;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">;

// `white` maps to `btn-default`, NOT inspinia's `btn-white`. `btn-white` is only
// defined in `admin/styles/style.css`, which nothing loads — `index.html` pulls in
// `custom.css`, which imports `bootstrap.min.css`, and that is where `btn-default`
// (white fill, #ccc border) comes from. Using `btn-white` renders an unstyled
// button.
const solidClassNames: Record<EntityModalButtonKind, string> = {
  blue: "btn-primary",
  orange: "btn-warning",
  red: "btn-danger",
  white: "btn-default",
};

// `btn-default` already reads as the outline/neutral button, and pairing it with
// `btn-outline` makes it transparent, so it keeps its solid class.
const outlineClassNames: Record<EntityModalButtonKind, string> = {
  blue: "btn-primary btn-outline",
  orange: "btn-warning btn-outline",
  red: "btn-danger btn-outline",
  white: "btn-default",
};

const linkColors: Record<EntityModalButtonKind, string> = {
  blue: "#3077eb",
  orange: "#f8ac59",
  red: "#ed5565",
  white: "inherit",
};

const sizeClassNames: Record<EntityModalButtonSize, string> = {
  md: "",
  sm: "btn-sm",
  xs: "btn-xs",
};

const LOADING_ICON_CLASS_NAME = "fa fa-circle-o-notch fa-spin fa-fw";

const iconOnlyStyleBySize: Record<EntityModalButtonSize, React.CSSProperties> = {
  md: { height: 34, minWidth: 34, width: 34 },
  sm: { height: 30, minWidth: 30, width: 30 },
  xs: { height: 24, minWidth: 24, width: 24 },
};

function getClassName(props: {
  className: string | undefined;
  kind: EntityModalButtonKind;
  size: EntityModalButtonSize;
  variant: EntityModalButtonVariant;
}) {
  const kindClassName =
    props.variant === "link"
      ? "btn-link"
      : props.variant === "outline"
      ? outlineClassNames[props.kind]
      : solidClassNames[props.kind];

  return ["btn", sizeClassNames[props.size], kindClassName, props.className]
    .filter(Boolean)
    .join(" ");
}

function getStyle(props: {
  isIconOnly: boolean;
  kind: EntityModalButtonKind;
  size: EntityModalButtonSize;
  style: React.CSSProperties | undefined;
  variant: EntityModalButtonVariant;
}): React.CSSProperties | undefined {
  // Link buttons are used inline (inside table cells and next to text), so they
  // drop the button box entirely rather than sitting in a padded button shell.
  if (props.variant === "link") {
    return {
      border: "none",
      color: linkColors[props.kind],
      padding: 0,
      verticalAlign: "baseline",
      ...props.style,
    };
  }

  if (props.isIconOnly) {
    return {
      alignItems: "center",
      display: "inline-flex",
      justifyContent: "center",
      padding: 0,
      ...iconOnlyStyleBySize[props.size],
      ...props.style,
    };
  }

  return props.style;
}

const EntityModalButton = React.forwardRef<HTMLButtonElement, Props>(function EntityModalButton(
  props,
  ref
) {
  const {
    children,
    className,
    disabled,
    iconClassName,
    isIconOnly = false,
    isLoading = false,
    kind = "white",
    size = "md",
    style,
    type = "button",
    variant = "solid",
    ...buttonProps
  } = props;
  const resolvedIconClassName = isLoading ? LOADING_ICON_CLASS_NAME : iconClassName;
  const hasIcon = resolvedIconClassName !== undefined;
  const hasChildren = children !== undefined && children !== null && children !== "";

  return (
    <button
      ref={ref}
      className={getClassName({ className, kind, size, variant })}
      disabled={disabled === true || isLoading}
      style={getStyle({ isIconOnly, kind, size, style, variant })}
      type={type}
      {...buttonProps}
    >
      {hasIcon ? <i className={resolvedIconClassName} /> : null}
      {hasIcon && hasChildren ? " " : null}
      {children}
    </button>
  );
});

export default EntityModalButton;

/** Bootstrap button group — renders attached buttons, e.g. a split button. */
export function EntityModalButtonGroup(props: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={["btn-group", props.className].filter(Boolean).join(" ")} style={props.style}>
      {props.children}
    </div>
  );
}
